import clsx from "clsx";
import type { ReactNode } from "react";
import {
  bestellnummerQuelleLabel,
  classifyBestellnummerQuelle,
  type BestellnummerQuelle,
} from "@/lib/bestellnummer";

export function BestellnummerQuelleBadge({
  quelle,
  className,
}: {
  quelle: BestellnummerQuelle;
  className?: string;
}) {
  const label = bestellnummerQuelleLabel(quelle);
  if (!label) return null;
  return (
    <span
      className={clsx(
        "eh-bn-badge",
        quelle === "regel" ? "eh-bn-badge--regel" : "eh-bn-badge--manuell",
        className,
      )}
    >
      {label}
    </span>
  );
}

export function BestellnummerField({
  id,
  value,
  onChange,
  disabled,
  placeholder = "Kunden-Bestellnummer …",
  quelle,
  regelWert,
  onApplyRegel,
  hint,
  hideLabel = false,
  /** Kürzere Darstellung: Herkunft steht am Badge, ohne Erklärungstext. */
  compact = false,
}: {
  id?: string;
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  placeholder?: string;
  quelle?: BestellnummerQuelle;
  regelWert?: string | null;
  onApplyRegel?: () => void;
  hint?: ReactNode;
  hideLabel?: boolean;
  compact?: boolean;
}) {
  const trimmed = value.trim();
  const regel = String(regelWert || "").trim();
  const resolvedQuelle = classifyBestellnummerQuelle(trimmed, regel, quelle);
  const showApply = Boolean(regel && trimmed !== regel);

  return (
    <div className="space-y-1.5">
      {!hideLabel ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <label className="eh-label mb-0" htmlFor={id}>
            Bestellnummer
          </label>
          <BestellnummerQuelleBadge quelle={resolvedQuelle} />
        </div>
      ) : resolvedQuelle ? (
        <div className="flex justify-end">
          <BestellnummerQuelleBadge quelle={resolvedQuelle} />
        </div>
      ) : null}
      <div className="relative">
        <input
          id={id}
          type="text"
          className={clsx(
            "eh-input w-full",
            trimmed && !disabled && "pr-16",
            resolvedQuelle === "regel" && "eh-bn-input--regel",
            resolvedQuelle === "manuell" && "eh-bn-input--manuell",
          )}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete="off"
        />
        {trimmed && !disabled ? (
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-semibold text-label-secondary hover:text-label"
            onClick={() => onChange("")}
          >
            Löschen
          </button>
        ) : null}
      </div>
      {showApply ? (
        <p className="text-xs text-label-secondary">
          Kundenregel: <span className="font-medium text-label">{regel}</span>
          {onApplyRegel && !disabled ? (
            <>
              {" "}
              <button
                type="button"
                className="font-semibold text-accent hover:underline"
                onClick={onApplyRegel}
              >
                Übernehmen
              </button>
            </>
          ) : null}
        </p>
      ) : compact ? null : resolvedQuelle === "regel" ? (
        <p className="text-xs text-label-tertiary">
          Automatisch aus den Kundenregeln — überschreiben oder leer löschen.
        </p>
      ) : resolvedQuelle === "manuell" ? (
        <p className="text-xs text-label-tertiary">Manuell hinterlegt, gilt für alle Dokumente dieses Projekts.</p>
      ) : hint ? (
        <p className="text-xs text-label-tertiary">{hint}</p>
      ) : (
        <p className="text-xs text-label-tertiary">
          Wird beim Anlegen aus den Kundenregeln vorausgefüllt, sobald Kunde, Ansprechpartner und Ort passen.
        </p>
      )}
    </div>
  );
}
